import { icons } from '@/components/common/Icon';

// Add tabSectionData to be used with the new TabSection component
export const tabSectionData = {
  title: "Compliance and Certifications",
  tabs: [
    { id: "compliance", title: "Compliance and Certifications" },
    { id: "cloud", title: "Cloud Infrastructure Security" },
    { id: "application", title: "Application and Product Security" },
    { id: "data", title: "Data Protection" },
    { id: "privacy", title: "Privacy and User Control" },
    { id: "access", title: "Secure Access Management" },
    { id: "continuity", title: "Business Continuity and Disaster Recovery" },
    { id: "disclosure", title: "Disclosure and Community Engagement" }
  ],
  tabContent: {
    compliance: {
      title: "Compliance and Certifications",
      description: "Adhering to global security benchmarks. Cobalt maintains strict compliance with international security standards",
      certifications: [
        {
          title: "SOC 2 Type II",
          description: "Robust controls for security, availability, and confidentiality"
        },
        {
          title: "ISO 27001",
          description: "Internationally recognized information security management practices"
        },
        {
          title: "GDPR & CCPA",
          description: "Comprehensive data privacy protections and compliance"
        },
        {
          title: "HIPAA Compliance",
          description: "Secure handling of healthcare data"
        }
      ]
    },
    cloud: {
      title: "Cloud Infrastructure Security",
      description: "Our cloud infrastructure is designed with security as the foundation",
      certifications: [
        {
          title: "Encrypted Data Storage",
          description: "All data is encrypted at rest and in transit using industry-standard encryption"
        },
        {
          title: "Regular Security Audits",
          description: "Continuous monitoring and regular penetration testing"
        },
        {
          title: "Multi-Region Redundancy",
          description: "Data distributed across multiple geographic regions for high availability"
        }
      ]
    },
    application: {
      title: "Application and Product Security",
      description: "Security built into every layer of our applications",
      certifications: [
        {
          title: "Secure Development Lifecycle",
          description: "Security integrated throughout the development process"
        },
        {
          title: "Vulnerability Management",
          description: "Regular scanning and remediation of vulnerabilities"
        },
        {
          title: "Third-party Security Reviews",
          description: "Independent security assessments of our applications"
        }
      ]
    },
    data: {
      title: "Data Protection",
      description: "Comprehensive measures to protect your sensitive data",
      certifications: [
        {
          title: "End-to-End Encryption",
          description: "Data protected throughout its lifecycle"
        },
        {
          title: "Data Loss Prevention",
          description: "Controls to prevent unauthorized access or leakage of sensitive data"
        },
        {
          title: "Secure Data Deletion",
          description: "Proper data destruction when no longer needed"
        }
      ]
    },
    privacy: {
      title: "Privacy and User Control",
      description: "Empowering users with control over their data",
      certifications: [
        {
          title: "Privacy by Design",
          description: "Privacy considerations built into all products and processes"
        },
        {
          title: "User Consent Management",
          description: "Clear options for users to control their data"
        },
        {
          title: "Transparency Reporting",
          description: "Clear documentation of our privacy practices"
        }
      ]
    },
    access: {
      title: "Secure Access Management",
      description: "Rigorous controls to manage system access",
      certifications: [
        {
          title: "Multi-Factor Authentication",
          description: "Additional security layers beyond passwords"
        },
        {
          title: "Principle of Least Privilege",
          description: "Access restricted to only what's necessary"
        },
        {
          title: "Regular Access Reviews",
          description: "Periodic verification of access rights"
        }
      ]
    },
    continuity: {
      title: "Business Continuity and Disaster Recovery",
      description: "Ensuring service availability even during unexpected events",
      certifications: [
        {
          title: "Comprehensive Backup Strategy",
          description: "Regular backups with secure off-site storage"
        },
        {
          title: "Disaster Recovery Planning",
          description: "Documented procedures for rapid service restoration"
        },
        {
          title: "Regular Recovery Testing",
          description: "Simulated disaster scenarios to validate recovery procedures"
        }
      ]
    },
    disclosure: {
      title: "Disclosure and Community Engagement",
      description: "Commitment to transparency and security collaboration",
      certifications: [
        {
          title: "Responsible Disclosure Program",
          description: "Clear process for reporting security vulnerabilities"
        },
        {
          title: "Security Community Participation",
          description: "Active engagement with security researchers"
        },
        {
          title: "Transparent Security Communications",
          description: "Timely notifications about security incidents"
        }
      ]
    }
  }
};

// Define trustCardData
export const trustCardData = {
  title: "Transparency and Trust",
  description: "Visit our Trust Center for detailed reports, compliance documentation, and ongoing security updates to ensure your confidence in Cobalt's security practices.",
  ctaText: "Privacy Policy",
  ctaHref: "/privacy-policy",
  link1Text: "Support Policy",
  link1Href: "/support-policy",
  link2Text: "Cookie Policy",
  link2Href: "/cookie-policy"
};

export const securityFeatures = [
  {
    title: 'End-to-End Encryption',
    description: 'All data transmitted through Cobalt is encrypted using industry-standard protocols.',
    iconPath: 'https://cobalt-app-logos.s3.ap-south-1.amazonaws.com/website_images/Security/encryption-icon.svg',
  },
  {
    title: 'SOC 2 Compliance',
    description: 'Cobalt maintains SOC 2 Type II compliance, ensuring your data is handled securely.',
    iconPath: 'https://cobalt-app-logos.s3.ap-south-1.amazonaws.com/website_images/Security/compliance-icon.svg',
  },
  {
    title: 'Role-Based Access Control',
    description: 'Granular permission settings let you control exactly who can access what.',
    iconPath: 'https://cobalt-app-logos.s3.ap-south-1.amazonaws.com/website_images/Security/rbac-icon.svg',
  },
  {
    title: 'Regular Security Audits',
    description: 'We conduct continuous security testing and third-party penetration tests.',
    iconPath: 'https://cobalt-app-logos.s3.ap-south-1.amazonaws.com/website_images/Security/audit-icon.svg',
  },
  {
    title: 'Data Residency Options',
    description: 'Choose where your data is stored to meet compliance requirements.',
    iconPath: 'https://cobalt-app-logos.s3.ap-south-1.amazonaws.com/website_images/Security/data-residency-icon.svg',
  }
];

export const securityData = {
  hero: {
    tag: {
      text: 'SECURITY',
      href: '/security',
    },
    title: "Enterprise-Grade Security for Your Integrations",
    description: "At Cobalt, security isn't just a feature—it's foundational to everything we build. Our platform is designed with security and compliance at its core.",
    cta: {
      primary: {
        text: 'View Trust Center',
        href: 'https://security.gocobalt.io/',
      },
      secondary: {
        text: 'Book a Demo',
        href: 'https://cal.com/cobalt/welcome-to-cobalt',
      }
    },
    backgroundImage: 'https://cobalt-app-logos.s3.ap-south-1.amazonaws.com/website_images/Security+page+icons+and+images/Hero+image-security+page.svg'
  },

  policies: {
    supportPolicy: {
      text: 'Support Policy',
      href: '/support-policy'
    },
    cookiePolicy: {
      text: 'Cookie Policy',
      href: '/cookie-policy'
    }
  },

  bento: {
    title: "Our Security Commitments",
    description: "We build security into every aspect of our platform",
    items: [
      {
        title: "Data Protection",
        description: "We employ industry-leading encryption for data at rest and in transit",
        icon: "https://cobalt-app-logos.s3.ap-south-1.amazonaws.com/website_images/Security+page+icons+and+images/Data+centers+icon.svg"
      },
      {
        title: "Compliance",
        description: "SOC 2 Type II certified with regular third-party audits",
        icon: "check" as keyof typeof icons
      },
      {
        title: "Access Controls",
        description: "Fine-grained permissions and role-based access controls",
        icon: "lock" as keyof typeof icons
      },
      {
        title: "Monitoring",
        description: "24/7 monitoring with automated threat detection systems",
        icon: "eye" as keyof typeof icons
      },
      {
        title: "Infrastructure",
        description: "Secure cloud infrastructure with redundancy and disaster recovery",
        icon: "server" as keyof typeof icons
      },
      {
        title: "Transparency",
        description: "Clear documentation and incident response procedures",
        icon: "fileText" as keyof typeof icons
      }
    ]
  },

  compliance: {
    title: "Compliance & Certifications",
    description: "We maintain rigorous compliance with industry standards",
    certifications: [
      {
        name: "SOC 2 Type II",
        logo: "https://cobalt-app-logos.s3.ap-south-1.amazonaws.com/website_images/Security/soc2-logo.svg",
        description: "Certified compliance with SOC 2 Type II standards for security, availability, and confidentiality."
      },
      {
        name: "GDPR",
        logo: "https://cobalt-app-logos.s3.ap-south-1.amazonaws.com/website_images/Security/gdpr-logo.svg",
        description: "Fully compliant with GDPR requirements for data protection and privacy."
      },
      {
        name: "HIPAA",
        logo: "https://cobalt-app-logos.s3.ap-south-1.amazonaws.com/website_images/Security/hipaa-logo.svg",
        description: "Designed to help customers maintain HIPAA compliance for healthcare data."
      },
      {
        name: "ISO 27001",
        logo: "https://cobalt-app-logos.s3.ap-south-1.amazonaws.com/website_images/Security/iso-logo.svg",
        description: "In process of ISO 27001 certification for information security management."
      }
    ]
  },

  securityProcess: {
    title: "Our Security Development Lifecycle",
    description: "Security is integrated into every step of our development process",
    steps: [
      {
        number: "01",
        title: "Design",
        description: "Security requirements and threat modeling are incorporated from the start."
      },
      {
        number: "02",
        title: "Development",
        description: "Secure coding practices and automated security testing during development."
      },
      {
        number: "03",
        title: "Testing",
        description: "Rigorous security testing, including vulnerability scanning and penetration testing."
      },
      {
        number: "04",
        title: "Deployment",
        description: "Secure deployment processes with final security validation."
      },
      {
        number: "05",
        title: "Monitoring",
        description: "Continuous monitoring and responsive security updates."
      }
    ]
  },

  faq: {
    title: "Security FAQ",
    description: "Common questions about Cobalt's security practices",
    questions: [
      {
        question: "How does Cobalt protect my data?",
        answer: "We use industry-standard encryption for all data at rest and in transit. Our platform is built with a security-first approach, employing best practices at every level."
      },
      {
        question: "Is Cobalt SOC 2 compliant?",
        answer: "Yes, Cobalt maintains SOC 2 Type II compliance, verified through independent audits to ensure we meet rigorous security, availability, and confidentiality standards."
      },
      {
        question: "How does Cobalt handle user authentication?",
        answer: "We support various authentication methods including SSO, 2FA, and SAML to ensure secure access to your account and data."
      },
      {
        question: "What happens in case of a security incident?",
        answer: "We have a comprehensive incident response plan in place, with procedures for prompt notification, containment, and remediation."
      },
      {
        question: "Can I get more information about Cobalt's security practices?",
        answer: "Yes, we provide detailed security documentation to customers. Contact our team to request our security whitepaper."
      }
    ]
  },

  contentCallout: {
    title: "Transparency and Trust",
    card: {
      title: "Visit our Trust Center",
      description: "Visit our Trust Center for detailed reports, compliance documentation, and ongoing security updates to ensure your confidence in Cobalt's security practices."
    },
    cta: {
      text: "View Trust",
      href: "https://security.gocobalt.io/"
    }
  },

  cta: {
    title: "The future of software is seamlessly Integrated",
    description: "Be ready with Cobalt's embedded integration platform",
    cta: {
      text: "Get Started",
      href: "/sign-up",
      secondary: {
        text: "Book a Demo",
        href: "https://cal.com/cobalt/welcome-to-cobalt"
      }
    }
  },

  bentoGrid: {
    title: "Delivering world-class security and privacy standards",
    subtitle: "Cobalt maintains strict compliance with international security standards",
    securityFeatures: [
      {
        title: "Uptime",
        description: "Fault-tolerant infrastructure ensures availability even during extreme demand",
        icon: "https://cobalt-app-logos.s3.ap-south-1.amazonaws.com/website_images/Security+page+icons+and+images/Uptime+icon.svg"
      },
      {
        title: "Encryption",
        description: "Data is encrypted at rest and in transit, and PII is protected with an additional layer of application encryption",
        icon: "https://cobalt-app-logos.s3.ap-south-1.amazonaws.com/website_images/Security+page+icons+and+images/Encryption+icon.svg"
      },
      {
        title: "Data centers",
        description: "All data is secured in Amazon Web Services (AWS) data centers with enterprise-grade physical and network security",
        icon: "https://cobalt-app-logos.s3.ap-south-1.amazonaws.com/website_images/Security+page+icons+and+images/Data+centers+icon.svg"
      }
    ],
    compliance: {
      title: "Compliance frameworks",
      description: "Cobalt adheres to industry-standard compliance frameworks, including SOC 2 Type II, ISO 27001, HIPAA, GDPR, and CCPA",
      badges: [
        {
          title: "SOC 2 Type 2",
          logo: "https://cobalt-app-logos.s3.ap-south-1.amazonaws.com/website_images/Security/soc2-logo.svg"
        },
        {
          title: "ISO 27001 v2022",
          logo: "https://cobalt-app-logos.s3.ap-south-1.amazonaws.com/website_images/Security/iso27001-logo.svg"
        },
        {
          title: "VAPT",
          logo: "https://cobalt-app-logos.s3.ap-south-1.amazonaws.com/website_images/Security/vapt-logo.svg"
        },
        {
          title: "GDPR",
          logo: "https://cobalt-app-logos.s3.ap-south-1.amazonaws.com/website_images/Security/gdpr-logo.svg"
        }
      ]
    }
  },

  // Add TabSection data
  tabSection: {
    title: "Security at Cobalt",
    subtitle: "Built to meet your security needs",
    description: "Prioritizing your data security and privacy with comprehensive measures. Cobalt ensures the highest standards of security and privacy, embedding rigorous protocols at every layer of our infrastructure and processes.",
    tag: "Enterprise-level security & privacy",
    tabs: [
      { id: "compliance", title: "Compliance and Certifications" },
      { id: "cloud", title: "Cloud Infrastructure Security" },
      { id: "application", title: "Application and Product Security" },
      { id: "data", title: "Data Protection" },
      { id: "privacy", title: "Privacy and User Control" },
      { id: "access", title: "Secure Access Management" },
      { id: "monitoring", title: "Security Monitoring & Incident Response" },
      { id: "continuity", title: "Business Continuity and Disaster Recovery" },
      { id: "disclosure", title: "Responsible Disclosure and Community Engagement" },
      { id: "transparency", title: "Transparency and Trust" }
    ],
    tabContent: {
      compliance: {
        title: "Compliance and Certifications",
        description: "Adhering to global security benchmarks. Cobalt maintains strict compliance with international security standards:",
        certifications: [
          {
            title: "SOC 2 Type II",
            description: "Robust controls for security, availability, and confidentiality"
          },
          {
            title: "ISO 27001",
            description: "Internationally recognized information security management practices"
          },
          {
            title: "GDPR & CCPA",
            description: "Comprehensive data privacy protections and compliance"
          },
          {
            title: "HIPAA Compliance",
            description: "Secure handling of healthcare data"
          }
        ]
      },
      cloud: {
        title: "Cloud Infrastructure Security",
        description: "Robust, resilient, and reliable. Cobalt leverages industry-leading cloud infrastructure to safeguard your data:",
        certifications: [
          {
            title: "Secure Hosting",
            description: "AWS data centers with cutting-edge security"
          },
          {
            title: "Data Encryption",
            description: "AES-256 encryption at rest, TLS 1.2+ in transit"
          },
          {
            title: "Continuous Vulnerability Management",
            description: "Regular penetration tests, dynamic (DAST) and static (SAST) security scans"
          }
        ]
      },
      application: {
        title: "Application and Product Security",
        description: "Securing every product lifecycle stage. Cobalt integrates advanced security practices into our software development:",
        certifications: [
          {
            title: "Secure Development Lifecycle (SDLC)",
            description: "Rigorous code reviews, automated testing, and proactive vulnerability monitoring"
          },
          {
            title: "Regular Penetration Testing",
            description: "Conducted by reputable third-party experts"
          },
          {
            title: "Dependency Monitoring",
            description: "Continuous scanning and management of software dependencies"
          }
        ]
      },
      data: {
        title: "Data Protection",
        description: "Advanced security for every piece of data. Your data is continuously protected through comprehensive measures:",
        certifications: [
          {
            title: "Role-Based Access Control (RBAC)",
            description: "Enforces strict access restrictions"
          },
          {
            title: "Selective Sync and Data Minimization",
            description: "Only necessary data is processed, minimizing risk exposure"
          },
          {
            title: "Detailed Audit Logging",
            description: "Comprehensive logs for visibility and accountability"
          },
          {
            title: "Data Anonymization",
            description: "Option for anonymizing data to enhance privacy"
          }
        ]
      },
      privacy: {
        title: "Privacy and User Control",
        description: "Full transparency and control over your data. Cobalt empowers users with complete control:",
        certifications: [
          {
            title: "Data Residency Options",
            description: "Choose regional or single-tenant hosting"
          },
          {
            title: "Privacy Compliance",
            description: "Simplified compliance with GDPR, CCPA, and other global privacy regulations"
          },
          {
            title: "Transparent Data Handling",
            description: "Clear policies and processes for data management"
          }
        ]
      },
      access: {
        title: "Secure Access Management",
        description: "Reliable, secure, and streamlined user access. Ensuring secure user management through advanced authentication protocols:",
        certifications: [
          {
            title: "Multi-Factor Authentication (MFA)",
            description: "Adds critical layers of protection"
          },
          {
            title: "Single Sign-On (SSO)",
            description: "Simplified integration with Okta, OAuth, and SAML"
          },
          {
            title: "Credential Security",
            description: "Regular rotation and encrypted storage of user credentials"
          }
        ]
      },
      monitoring: {
        title: "Security Monitoring & Incident Response",
        description: "Always vigilant, always ready. Proactively managing security threats and incidents:",
        certifications: [
          {
            title: "24/7 Security Monitoring",
            description: "Automated threat detection and rapid response systems"
          },
          {
            title: "Incident Response Protocols",
            description: "Clearly defined processes to handle security incidents swiftly"
          },
          {
            title: "Real-Time Logging and Alerts",
            description: "Immediate visibility into system activities"
          }
        ]
      },
      continuity: {
        title: "Business Continuity and Disaster Recovery",
        description: "Uninterrupted service, even in crises. Detailed contingency planning to ensure service continuity:",
        certifications: [
          {
            title: "Regular Backups",
            description: "Frequent encrypted backups"
          },
          {
            title: "Comprehensive Testing",
            description: "Routine disaster recovery simulations and drills"
          },
          {
            title: "Clear Recovery Objectives",
            description: "Defined and tested recovery time (RTO) and recovery point (RPO) objectives"
          }
        ]
      },
      disclosure: {
        title: "Responsible Disclosure and Community Engagement",
        description: "Collaborating for stronger security. Cobalt actively engages with the security community:",
        certifications: [
          {
            title: "Bug Bounty and Ethical Hacking",
            description: "Welcoming security researchers and ethical hackers"
          },
          {
            title: "Transparent Disclosure Policies",
            description: "Clear guidelines for reporting and resolving vulnerabilities"
          }
        ]
      },
      transparency: {
        title: "Transparency and Trust",
        description: "Open and transparent communication. Visit our Trust Center for detailed reports, compliance documentation, and ongoing security updates to ensure your confidence in Cobalt's security practices.",
        certifications: [
          {
            title: "Trust Center",
            description: "Comprehensive documentation of our security practices"
          },
          {
            title: "Ready to Enhance Your Security?",
            description: "Connect with us to experience unparalleled security practices with Cobalt. Request your demo or consultation today."
          }
        ]
      }
    }
  },

  // Add TrustCard data
  trustCard: trustCardData
};

export const bentoItems = [
  {
    title: "Data Protection",
    description: "We employ industry-leading encryption for data at rest and in transit",
    imageSrc: "https://cobalt-app-logos.s3.ap-south-1.amazonaws.com/website_images/Security/data-protection.svg",
    imageAlt: "Data Protection Illustration"
  },
  {
    title: "Compliance",
    description: "SOC 2 Type II certified with regular third-party audits",
    imageSrc: "https://cobalt-app-logos.s3.ap-south-1.amazonaws.com/website_images/Security/compliance.svg",
    imageAlt: "Compliance Illustration"
  },
  {
    title: "Access Controls",
    description: "Fine-grained permissions and role-based access controls",
    imageSrc: "https://cobalt-app-logos.s3.ap-south-1.amazonaws.com/website_images/Security/access-control.svg",
    imageAlt: "Access Control Illustration"
  },
  {
    title: "Monitoring",
    description: "24/7 monitoring with automated threat detection systems",
    imageSrc: "https://cobalt-app-logos.s3.ap-south-1.amazonaws.com/website_images/Security/monitoring.svg",
    imageAlt: "Monitoring Illustration"
  },
  {
    title: "Infrastructure",
    description: "Secure cloud infrastructure with redundancy and disaster recovery",
    imageSrc: "https://cobalt-app-logos.s3.ap-south-1.amazonaws.com/website_images/Security/infrastructure.svg",
    imageAlt: "Infrastructure Illustration"
  },
  {
    title: "Transparency",
    description: "Clear documentation and incident response procedures",
    imageSrc: "https://cobalt-app-logos.s3.ap-south-1.amazonaws.com/website_images/Security/transparency.svg",
    imageAlt: "Transparency Illustration"
  }
];